"use client";

import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Image as ImageIcon, X } from "lucide-react";
import { ChatImageGroup } from "@/components/chat/ChatImageGroup";
import { GLASS_WHITE_BUTTON } from "@/components/chat/GlassComposer";
import { ImageModal } from "@/components/ImageModal";
import { createClient } from "@/lib/supabase/client";
import {
  SUPPORT_IMAGE_MAX_FILES,
  SUPPORT_IMAGE_TYPES,
  supportMessageImages,
} from "@/lib/chat/support-attachments";
import { useSignedChatAttachmentUrls } from "@/hooks/useSignedChatAttachmentUrls";
import type { SupportImageDraftItem } from "@/hooks/useSupportImageDraft";
import type { SupportMessage } from "@/types/support";

/** 입력창 왼쪽 사진 버튼. */
export function SupportImageButton({
  count,
  onAdd,
}: {
  count: number;
  onAdd: (files: File[]) => void;
}) {
  const t = useTranslations("support.composer");
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={SUPPORT_IMAGE_TYPES.join(",")}
        multiple
        className="hidden"
        onChange={(e) => {
          onAdd(Array.from(e.target.files ?? []));
          e.target.value = ""; // 같은 사진을 다시 고를 수 있게
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={count >= SUPPORT_IMAGE_MAX_FILES}
        className={GLASS_WHITE_BUTTON}
        title={t("addImage")}
        aria-label={t("addImage")}
      >
        <ImageIcon className="h-4 w-4" />
      </button>
    </>
  );
}

/** 보내기 전 사진 줄 — 입력창 바로 위. */
export function SupportImageTray({
  items,
  onRemove,
}: {
  items: SupportImageDraftItem[];
  onRemove: (id: string) => void;
}) {
  const t = useTranslations("support.composer");
  if (items.length === 0) return null;
  return (
    <div className="mb-1 flex gap-2 overflow-x-auto px-0.5 pt-1.5 pb-1">
      {items.map((item) => (
        <div key={item.id} className="relative size-16 shrink-0">
          {/* eslint-disable-next-line @next/next/no-img-element -- blob: 미리보기 */}
          <img
            src={item.previewUrl}
            alt={item.file.name}
            className="size-full rounded-[10px] object-cover"
          />
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full bg-gray-900/75 text-white"
            aria-label={t("removeImage")}
            title={t("removeImage")}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

export interface SupportImageViewer {
  resolve: (url: string) => string | undefined;
  open: (images: { url: string; name: string }[], index: number) => void;
  /** 전체 화면 뷰어 — 목록 어딘가에 한 번 그린다. */
  modal: ReactNode;
}

/** 대화 목록의 사진 서명과 전체 화면 뷰어. */
export function useSupportImageViewer(
  messages: ReadonlyArray<Pick<SupportMessage, "conversation_id" | "attachments">>,
): SupportImageViewer {
  const t = useTranslations("support.composer");
  const supabase = useMemo(() => createClient(), []);
  // 규칙을 통과한 사진만 서명한다.
  const signable = useMemo(
    () =>
      messages.map((message) => ({
        attachments: supportMessageImages(message).map((image) => ({ ...image, type: "image" })),
      })),
    [messages],
  );
  const { resolve } = useSignedChatAttachmentUrls(signable, supabase);
  const [viewing, setViewing] = useState<{
    images: { url: string; name: string }[];
    index: number;
  } | null>(null);

  return {
    resolve,
    open: (images, index) => setViewing({ images, index }),
    modal: viewing ? (
      <ImageModal
        images={viewing.images.map((image) => ({
          ...image,
          url: resolve(image.url) ?? image.url,
        }))}
        currentIndex={viewing.index}
        isOpen
        onClose={() => setViewing(null)}
        onIndexChange={(index) => setViewing((v) => (v ? { ...v, index } : v))}
        labels={{
          imageViewer: t("imageViewer"),
          previousImage: t("previousImage"),
          nextImage: t("nextImage"),
          closeImage: t("closeImage"),
        }}
      />
    ) : null,
  };
}

/** 메시지의 사진 묶음. 그릴 사진이 없으면 아무것도 그리지 않는다. */
export function SupportMessageImages({
  message,
  viewer,
}: {
  message: Pick<SupportMessage, "conversation_id" | "attachments">;
  viewer: SupportImageViewer;
}) {
  const images = supportMessageImages(message);
  if (images.length === 0) return null;
  return (
    <ChatImageGroup
      images={images}
      resolveUrl={viewer.resolve}
      onOpen={(index) => viewer.open(images, index)}
    />
  );
}
